import type {DependencyList} from 'react';
import {useEffect, useState} from 'react';
import {useIsMount} from '../useIsMount';

export interface UseFunctionSubscribeOptions {
  /** When true, the subscription is not started and state is reset. */
  skip?: boolean;
  /** Dependencies that trigger re-subscription when changed. Defaults to [subscribe]. */
  deps?: DependencyList;
}

export interface UseFunctionSubscribeResult<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Subscribes to a source that pushes data via callbacks and returns an unsubscribe.
 * Manages loading/error state and unsubscribes on unmount or when deps change.
 *
 * @param subscribe - Function that receives onData and onError and returns an unsubscribe function. Make sure to useCallback to lock it
 * @param options - skip: do not subscribe; deps: re-subscribe when these change.
 */
export function useFunctionSubscribe<T>(
  subscribe: (onData: (data: T) => void, onError: (err: Error) => void) => () => void,
  options: UseFunctionSubscribeOptions = {},
): UseFunctionSubscribeResult<T> {
  const {skip = false, deps = []} = options;
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<Error | undefined>(undefined);
  const isMounted = useIsMount();

  useEffect(() => {
    if (skip) {
      setData(undefined);
      setLoading(false);
      setError(undefined);
      return;
    }
    setLoading(true);
    setError(undefined);
    const unsubscribe = subscribe(
      (value) => {
        if (isMounted()) {
          setData(value);
          setLoading(false);
          setError(undefined);
        }
      },
      (err) => {
        if (isMounted()) {
          setError(err);
          setLoading(false);
          setData(undefined);
        }
      },
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, isMounted, ...(deps as unknown[])]);

  return {
    data,
    loading,
    error,
  };
}
